import React, {useState, useEffect} from 'react';
import {
    Database,
    Plus,
    Download,
    Trash2,
    Loader2,
    AlertTriangle,
    RefreshCw,
    Search,
    Clock,
    HardDrive,
    CheckCircle,
    XCircle,
    Archive,
    List,
    FileArchive
} from 'lucide-react';

const DatabaseBackupsPage = () => {
    const [collections, setCollections] = useState([]);
    const [backups, setBackups] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    
    // Create backup modal
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [selectedCollections, setSelectedCollections] = useState([]);
    const [backupDescription, setBackupDescription] = useState('');
    const [isCreating, setIsCreating] = useState(false);
    
    // Backup status polling
    const [pollingBackupId, setPollingBackupId] = useState(null);

    const apiUrl = 'https://api.digitalaalim.com';
    const accessToken = localStorage.getItem('accessToken');

    useEffect(() => {
        fetchCollections();
        fetchBackups();
    }, []);

    // Poll backup status
    useEffect(() => {
        if (pollingBackupId) {
            const interval = setInterval(() => {
                checkBackupStatus(pollingBackupId);
            }, 3000);
            return () => clearInterval(interval);
        }
    }, [pollingBackupId]);


    const fetchCollections = async () => {
        try {
            const response = await fetch(`${apiUrl}/admin/milvus/collections`, {
                headers: {
                    'Authorization': `Bearer ${accessToken}`,
                },
            });

            if (!response.ok) {
                throw new Error('Failed to fetch collections');
            }

            const data = await response.json();
            setCollections(data.collections || []);
        } catch (error) {
            console.error('Error fetching collections:', error);
        }
    };

    const fetchBackups = async () => {
        setIsLoading(true);
        setError(null);

        try {
            const response = await fetch(`${apiUrl}/admin/milvus/backups`, {
                headers: {
                    'Authorization': `Bearer ${accessToken}`,
                },
            });

            if (!response.ok) {
                throw new Error('Failed to fetch backups');
            }

            const data = await response.json();
            setBackups(data.backups || []);
        } catch (error) {
            console.error('Error fetching backups:', error);
            setError(error.message);
        } finally {
            setIsLoading(false);
        }
    };

    const createBackup = async () => {
        setIsCreating(true);

        try {
            const payload = {
                collection_names: selectedCollections.length > 0 ? selectedCollections : null,
                description: backupDescription.trim() || null
            };

            const response = await fetch(`${apiUrl}/admin/milvus/backup`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${accessToken}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(payload)
            });

            if (!response.ok) {
                throw new Error('Failed to create backup');
            }

            const data = await response.json();
            alert(`Backup started successfully! Backup ID: ${data.backup_id}`);
            
            // Start polling for this backup
            setPollingBackupId(data.backup_id);
            
            closeCreateModal();
            fetchBackups();
        } catch (error) {
            console.error('Error creating backup:', error);
            alert(`Failed to create backup: ${error.message}`);
        } finally {
            setIsCreating(false);
        }
    };


    const checkBackupStatus = async (backupId) => {
        try {
            const response = await fetch(`${apiUrl}/admin/milvus/backup/${backupId}/status`, {
                headers: {
                    'Authorization': `Bearer ${accessToken}`,
                },
            });

            if (!response.ok) return;

            const data = await response.json();
            
            // Update backup in list
            setBackups(prev => prev.map(b => 
                b.backup_id === backupId ? {...b, status: data.status} : b
            ));

            // Stop polling if completed or failed
            if (data.status === 'completed' || data.status === 'failed') {
                setPollingBackupId(null);
                fetchBackups();
            }
        } catch (error) {
            console.error('Error checking backup status:', error);
        }
    };

    const downloadBackup = async (backupId) => {
        try {
            const response = await fetch(`${apiUrl}/admin/milvus/backup/${backupId}/download`, {
                headers: {
                    'Authorization': `Bearer ${accessToken}`,
                },
            });

            if (!response.ok) {
                throw new Error('Failed to download backup');
            }

            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `milvus_backup_${backupId}.zip`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Error downloading backup:', error);
            alert(`Failed to download backup: ${error.message}`);
        }
    };

    const deleteBackup = async (backupId) => {
        const confirmed = window.confirm('Are you sure you want to delete this backup? This action cannot be undone.');
        if (!confirmed) return;

        try {
            const response = await fetch(`${apiUrl}/admin/milvus/backup/${backupId}`, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${accessToken}`,
                },
            });

            if (!response.ok) {
                throw new Error('Failed to delete backup');
            }

            alert('Backup deleted successfully!');
            fetchBackups();
        } catch (error) {
            console.error('Error deleting backup:', error);
            alert(`Failed to delete backup: ${error.message}`);
        }
    };


    const openCreateModal = () => {
        setSelectedCollections([]);
        setBackupDescription('');
        setShowCreateModal(true);
    };

    const closeCreateModal = () => {
        setShowCreateModal(false);
        setSelectedCollections([]);
        setBackupDescription('');
    };

    const toggleCollection = (collectionName) => {
        setSelectedCollections(prev => {
            if (prev.includes(collectionName)) {
                return prev.filter(c => c !== collectionName);
            } else {
                return [...prev, collectionName];
            }
        });
    };

    const selectAllCollections = () => {
        if (selectedCollections.length === collections.length) {
            setSelectedCollections([]);
        } else {
            setSelectedCollections(collections.map(c => c.name));
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const getStatusBadge = (status) => {
        const badges = {
            completed: { color: 'bg-green-100 text-green-800', icon: CheckCircle },
            processing: { color: 'bg-blue-100 text-blue-800', icon: Loader2 },
            pending: { color: 'bg-yellow-100 text-yellow-800', icon: Clock },
            failed: { color: 'bg-red-100 text-red-800', icon: XCircle }
        };

        const badge = badges[status] || badges.pending;
        const Icon = badge.icon;

        return (
            <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${badge.color}`}>
                <Icon size={12} className={`mr-1 ${status === 'processing' ? 'animate-spin' : ''}`}/>
                {status.charAt(0).toUpperCase() + status.slice(1)}
            </span>
        );
    };

    const filteredBackups = backups.filter(backup =>
        backup.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        backup.backup_id.toLowerCase().includes(searchTerm.toLowerCase()) ||
        backup.collections?.some(c => c.toLowerCase().includes(searchTerm.toLowerCase()))
    );


    return (
        <div className="space-y-8">
            {/* Header */}
            <div>
                <h1 className="text-3xl font-bold text-gray-800 flex items-center gap-2">
                    <Database className="text-blue-600" size={32}/>
                    Database Backups
                </h1>
                <p className="mt-2 text-gray-600">
                    Manage Milvus vector database backups and restore operations
                </p>
            </div>

            {/* Collections Overview */}
            <div className="grid md:grid-cols-3 gap-6">
                <div className="bg-white rounded-xl shadow-sm p-6">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm font-medium text-gray-600">Total Collections</p>
                            <p className="text-3xl font-bold text-gray-900 mt-1">{collections.length}</p>
                        </div>
                        <div className="p-3 bg-blue-100 rounded-lg">
                            <List size={24} className="text-blue-600"/>
                        </div>
                    </div>
                </div>

                <div className="bg-white rounded-xl shadow-sm p-6">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm font-medium text-gray-600">Total Backups</p>
                            <p className="text-3xl font-bold text-gray-900 mt-1">{backups.length}</p>
                        </div>
                        <div className="p-3 bg-green-100 rounded-lg">
                            <Archive size={24} className="text-green-600"/>
                        </div>
                    </div>
                </div>

                <div className="bg-white rounded-xl shadow-sm p-6">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm font-medium text-gray-600">Active Backups</p>
                            <p className="text-3xl font-bold text-gray-900 mt-1">
                                {backups.filter(b => b.status === 'processing' || b.status === 'pending').length}
                            </p>
                        </div>
                        <div className="p-3 bg-yellow-100 rounded-lg">
                            <Loader2 size={24} className="text-yellow-600"/>
                        </div>
                    </div>
                </div>
            </div>

            {/* Controls */}
            <div className="bg-white rounded-xl shadow-sm p-6">
                <div className="flex flex-col md:flex-row gap-4 items-start md:items-center justify-between">
                    <div className="relative w-full md:w-80">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <Search size={16} className="text-gray-400"/>
                        </div>
                        <input
                            type="text"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            placeholder="Search backups..."
                            className="pl-10 w-full border border-gray-300 rounded-md py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                    </div>

                    <div className="flex items-center gap-2 w-full md:w-auto">
                        <button
                            onClick={fetchBackups}
                            className="inline-flex items-center px-3 py-2 text-sm font-medium text-blue-700 bg-blue-50 rounded-md hover:bg-blue-100 transition-colors"
                        >
                            <RefreshCw size={14} className="mr-1"/>
                            Refresh
                        </button>

                        <button
                            onClick={openCreateModal}
                            className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 transition-colors"
                        >
                            <Plus size={16} className="mr-1"/>
                            Create Backup
                        </button>
                    </div>
                </div>
            </div>


            {/* Backups List */}
            {isLoading ? (
                <div className="flex justify-center items-center h-64">
                    <Loader2 size={40} className="animate-spin text-blue-600"/>
                </div>
            ) : error ? (
                <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-lg">
                    <div className="flex items-center">
                        <AlertTriangle size={24} className="text-red-500 mr-3"/>
                        <div>
                            <h3 className="text-red-800 font-medium">Error loading backups</h3>
                            <p className="text-red-700">{error}</p>
                        </div>
                    </div>
                </div>
            ) : (
                <div className="bg-white rounded-xl shadow-sm overflow-hidden">
                    {filteredBackups.length > 0 ? (
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Backup Info
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Collections
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Size
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Status
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Created
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Actions
                                    </th>
                                </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                {filteredBackups.map((backup) => (
                                    <tr key={backup.backup_id} className="hover:bg-gray-50">
                                        <td className="px-6 py-4">
                                            <div className="flex items-start">
                                                <div className="flex-shrink-0 h-10 w-10 rounded bg-blue-100 flex items-center justify-center">
                                                    <FileArchive className="h-5 w-5 text-blue-600"/>
                                                </div>
                                                <div className="ml-4">
                                                    <div className="text-sm font-medium text-gray-900">
                                                        {backup.description || 'Untitled Backup'}
                                                    </div>
                                                    <div className="text-xs text-gray-500 font-mono">
                                                        ID: {backup.backup_id.substring(0, 8)}...
                                                    </div>
                                                    {backup.created_by && (
                                                        <div className="text-xs text-gray-500">
                                                            By: {backup.created_by}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="text-sm text-gray-900">
                                                {backup.collection_count} collection{backup.collection_count !== 1 ? 's' : ''}
                                            </div>
                                            {backup.collections && backup.collections.length > 0 && (
                                                <div className="text-xs text-gray-500 mt-1">
                                                    {backup.collections.slice(0, 2).join(', ')}
                                                    {backup.collections.length > 2 && ` +${backup.collections.length - 2} more`}
                                                </div>
                                            )}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="flex items-center text-sm text-gray-900">
                                                <HardDrive size={14} className="mr-1 text-gray-400"/>
                                                {backup.file_size_mb > 0 
                                                    ? `${backup.file_size_mb.toFixed(2)} MB`
                                                    : 'N/A'}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            {getStatusBadge(backup.status)}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="flex items-center text-sm text-gray-500">
                                                <Clock size={14} className="mr-1"/>
                                                {formatDate(backup.created_at)}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                            <div className="flex items-center gap-2">
                                                {backup.status === 'completed' && (
                                                    <button
                                                        onClick={() => downloadBackup(backup.backup_id)}
                                                        className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-green-700 bg-green-100 rounded hover:bg-green-200 transition-colors"
                                                        title="Download"
                                                    >
                                                        <Download size={14} className="mr-1"/>
                                                        Download
                                                    </button>
                                                )}
                                                <button
                                                    onClick={() => deleteBackup(backup.backup_id)}
                                                    className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-red-700 bg-red-100 rounded hover:bg-red-200 transition-colors"
                                                    title="Delete"
                                                >
                                                    <Trash2 size={14} className="mr-1"/>
                                                    Delete
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        <div className="text-center py-12">
                            <Database size={48} className="mx-auto text-gray-400 mb-4"/>
                            <h3 className="text-lg font-medium text-gray-800 mb-2">
                                {searchTerm ? 'No backups found' : 'No backups yet'}
                            </h3>
                            <p className="text-gray-600 mb-4">
                                {searchTerm
                                    ? 'Try adjusting your search'
                                    : 'Create your first database backup to get started'}
                            </p>
                            {!searchTerm && (
                                <button
                                    onClick={openCreateModal}
                                    className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 transition-colors"
                                >
                                    <Plus size={16} className="mr-1"/>
                                    Create Backup
                                </button>
                            )}
                        </div>
                    )}
                </div>
            )}


            {/* Create Backup Modal */}
            {showCreateModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                        <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between">
                            <h2 className="text-xl font-bold text-gray-800">Create Database Backup</h2>
                            <button
                                onClick={closeCreateModal}
                                className="text-gray-400 hover:text-gray-600"
                            >
                                <XCircle size={24}/>
                            </button>
                        </div>

                        <div className="p-6 space-y-6">
                            {/* Description */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Backup Description (Optional)
                                </label>
                                <input
                                    type="text"
                                    value={backupDescription}
                                    onChange={(e) => setBackupDescription(e.target.value)}
                                    placeholder="e.g., Weekly backup before update"
                                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                            </div>

                            {/* Collections Selection */}
                            <div>
                                <div className="flex items-center justify-between mb-3">
                                    <label className="block text-sm font-medium text-gray-700">
                                        Select Collections
                                    </label>
                                    <button
                                        onClick={selectAllCollections}
                                        className="text-sm text-blue-600 hover:text-blue-800"
                                    >
                                        {selectedCollections.length === collections.length ? 'Deselect All' : 'Select All'}
                                    </button>
                                </div>

                                {collections.length > 0 ? (
                                    <div className="border border-gray-200 rounded-lg max-h-64 overflow-y-auto">
                                        {collections.map((collection) => (
                                            <label
                                                key={collection.name}
                                                className="flex items-center p-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                                            >
                                                <input
                                                    type="checkbox"
                                                    checked={selectedCollections.includes(collection.name)}
                                                    onChange={() => toggleCollection(collection.name)}
                                                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                                />
                                                <div className="ml-3 flex-1">
                                                    <div className="text-sm font-medium text-gray-900">
                                                        {collection.name}
                                                    </div>
                                                    {collection.num_entities !== undefined && (
                                                        <div className="text-xs text-gray-500">
                                                            {collection.num_entities.toLocaleString()} entities
                                                        </div>
                                                    )}
                                                    {collection.description && (
                                                        <div className="text-xs text-gray-500">
                                                            {collection.description}
                                                        </div>
                                                    )}
                                                </div>
                                            </label>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="text-center py-8 text-gray-500">
                                        <Database size={40} className="mx-auto mb-2 text-gray-400"/>
                                        <p>No collections available</p>
                                    </div>
                                )}

                                <div className="mt-3 p-3 bg-blue-50 rounded-lg">
                                    <p className="text-xs text-blue-700">
                                        <strong>Note:</strong> If no collections are selected, all collections will be backed up.
                                    </p>
                                </div>
                            </div>

                            {/* Summary */}
                            <div className="bg-gray-50 rounded-lg p-4">
                                <h3 className="text-sm font-medium text-gray-700 mb-2">Backup Summary</h3>
                                <div className="space-y-1 text-sm text-gray-600">
                                    <div className="flex justify-between">
                                        <span>Collections to backup:</span>
                                        <span className="font-medium">
                                            {selectedCollections.length > 0 
                                                ? `${selectedCollections.length} selected`
                                                : 'All collections'}
                                        </span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span>Total collections:</span>
                                        <span className="font-medium">{collections.length}</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="sticky bottom-0 bg-gray-50 px-6 py-4 border-t flex justify-end gap-3">
                            <button
                                onClick={closeCreateModal}
                                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={createBackup}
                                disabled={isCreating}
                                className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50"
                            >
                                {isCreating ? (
                                    <>
                                        <Loader2 size={16} className="mr-2 animate-spin"/>
                                        Creating...
                                    </>
                                ) : (
                                    <>
                                        <Archive size={16} className="mr-2"/>
                                        Create Backup
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Info Card */}
            <div className="bg-blue-50 rounded-lg p-4 border border-blue-100">
                <div className="flex items-start">
                    <div className="flex-shrink-0">
                        <Database size={20} className="text-blue-600"/>
                    </div>
                    <div className="ml-3">
                        <h3 className="text-sm font-medium text-blue-800">About Database Backups</h3>
                        <div className="mt-2 text-sm text-blue-700">
                            <p>
                                Create backups of your Milvus vector database collections. Backups are stored as ZIP files
                                and can be downloaded for safekeeping. The backup process runs in the background and may take
                                several minutes depending on the size of your collections.
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DatabaseBackupsPage;
